<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useWizardStore } from '@/stores/wizard'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import type { AgencyChannelPreset, WizardChannel } from '@/types'

const wizard = useWizardStore()
const auth = useAuthStore()

const SECTIONS = ['Command', 'Tactical', 'Air-to-Ground'] as const
type Section = (typeof SECTIONS)[number]

const MODE_OPTIONS = [
  { label: 'Analog', value: 'A' },
  { label: 'Digital', value: 'D' },
  { label: 'Mixed', value: 'M' },
]

// ── Agency presets ────────────────────────────────────────────────────────────
const agencyPresets = ref<AgencyChannelPreset[]>([])
let presetsUnsub: Unsubscribe | null = null

onMounted(() => {
  const agencyId = auth.userProfile?.agency_id
  if (!agencyId) return
  presetsUnsub = onSnapshot(collection(db, 'agencies', agencyId, 'channel_presets'), (snap) => {
    agencyPresets.value = snap.docs
      .map((d) => ({ preset_id: d.id, ...d.data() } as AgencyChannelPreset))
      .sort((a, b) => a.order - b.order)
  })
})

onUnmounted(() => {
  presetsUnsub?.()
})

// ── Channel helpers ────────────────────────────────────────────────────────────
function channelsForSection(section: Section) {
  return wizard.step4.channels.filter((c) => c.assignment === section)
}

const addedPresetIds = computed(
  () => new Set(wizard.step4.channels.filter((c) => c.preset_id).map((c) => c.preset_id!)),
)

const availablePresets = computed(() =>
  agencyPresets.value.filter((p) => !addedPresetIds.value.has(p.preset_id)),
)

const MODE_LABELS: Record<string, string> = { A: 'Analog', D: 'Digital', M: 'Mixed' }
const MODE_ORDER = ['A', 'D', 'M']

const presetsByMode = computed(() => {
  const groups: { mode: string; label: string; presets: AgencyChannelPreset[] }[] = []
  for (const mode of MODE_ORDER) {
    const presets = availablePresets.value.filter((p) => p.mode === mode)
    if (presets.length) groups.push({ mode, label: MODE_LABELS[mode] ?? mode, presets })
  }
  // catch any unrecognised modes
  const known = new Set(MODE_ORDER)
  const others = availablePresets.value.filter((p) => !known.has(p.mode))
  if (others.length) groups.push({ mode: 'other', label: 'Other', presets: others })
  return groups
})

function removeChannel(id: string) {
  wizard.step4.channels = wizard.step4.channels.filter((c) => c.id !== id)
}

// ── Dialog state ──────────────────────────────────────────────────────────────
const dialogVisible = ref(false)
const dialogSection = ref<Section>('Command')
const showCustomForm = ref(false)

const customForm = ref<Omit<WizardChannel, 'id' | 'include' | 'is_custom' | 'preset_id'>>({
  name: '',
  assignment: 'Command',
  rx_freq: '',
  rx_tone: '',
  tx_freq: '',
  tx_tone: '',
  mode: 'A',
  remarks: '',
})

function openDialog(section: Section) {
  dialogSection.value = section
  showCustomForm.value = false
  dialogVisible.value = true
}

function closeDialog() {
  dialogVisible.value = false
  showCustomForm.value = false
}

function addPreset(preset: AgencyChannelPreset) {
  wizard.step4.channels.push({
    id: `preset-${preset.preset_id}-${Date.now()}`,
    name: preset.name,
    assignment: dialogSection.value,
    rx_freq: preset.rx_freq,
    rx_tone: preset.rx_tone,
    tx_freq: preset.tx_freq,
    tx_tone: preset.tx_tone,
    mode: preset.mode,
    remarks: preset.remarks,
    include: true,
    is_custom: false,
    preset_id: preset.preset_id,
  })
  closeDialog()
}

function openCustomForm() {
  customForm.value = {
    name: '',
    assignment: dialogSection.value,
    rx_freq: '',
    rx_tone: '',
    tx_freq: '',
    tx_tone: '',
    mode: 'A',
    remarks: '',
  }
  showCustomForm.value = true
}

function addCustomChannel() {
  if (!customForm.value.name.trim()) return
  wizard.step4.channels.push({
    id: `custom-${Date.now()}`,
    ...customForm.value,
    include: true,
    is_custom: true,
  })
  closeDialog()
}

const dialogTitle = computed(() =>
  showCustomForm.value
    ? `Custom channel — ${dialogSection.value}`
    : `Add channel — ${dialogSection.value}`,
)
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div>
      <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Communications</h2>
      <p class="font-mono text-xs text-outline mt-1">Step 4 of 4 — optional</p>
    </div>

    <!-- Channel sections -->
    <div class="flex flex-col gap-3">
      <div
        v-for="section in SECTIONS"
        :key="section"
        class="border border-border"
      >
        <!-- Section header -->
        <div class="px-4 py-2.5 bg-surface-container-low border-b border-border">
          <span class="font-mono text-[0.65rem] font-bold uppercase tracking-widest text-outline">
            {{ section }}
          </span>
        </div>

        <!-- Existing channel rows -->
        <div
          v-for="ch in channelsForSection(section)"
          :key="ch.id"
          class="flex items-center gap-3 px-4 py-2.5 border-b border-border"
        >
          <div class="flex-1 min-w-0">
            <span class="font-mono text-sm font-bold text-on-surface">{{ ch.name }}</span>
            <span v-if="ch.rx_freq || ch.tx_freq" class="font-mono text-xs text-outline ml-2">
              <span v-if="ch.rx_freq">RX {{ ch.rx_freq }}</span>
              <span v-if="ch.rx_freq && ch.tx_freq"> / </span>
              <span v-if="ch.tx_freq">TX {{ ch.tx_freq }}</span>
              <span
                v-if="ch.mode"
                class="ml-1.5 px-1.5 py-0.5 bg-surface-container-highest font-mono text-xs"
              >{{ ch.mode }}</span>
            </span>
            <span v-else-if="ch.remarks" class="font-mono text-xs text-outline ml-2">{{ ch.remarks }}</span>
          </div>
          <button
            type="button"
            class="text-outline hover:text-error transition-colors flex-shrink-0"
            @click="removeChannel(ch.id)"
          >
            <span class="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>

        <!-- Add button -->
        <div class="px-4 py-2.5">
          <button
            type="button"
            class="font-mono text-xs uppercase tracking-widest text-primary hover:text-on-surface font-bold flex items-center gap-1 transition-colors"
            @click="openDialog(section)"
          >
            <span class="material-symbols-outlined text-[16px]">add</span>
            Add channel
          </button>
        </div>
      </div>
    </div>

    <!-- Add channel dialog -->
    <FosDialog
      :open="dialogVisible"
      :title="dialogTitle"
      max-width="max-w-lg"
      @update:open="(v) => { if (!v) closeDialog() }"
    >
      <!-- Preset list view -->
      <template v-if="!showCustomForm">
        <div class="flex flex-col -mx-6 -mt-2">
          <template v-if="presetsByMode.length > 0">
            <div class="flex divide-x divide-border border-b border-border">
              <div
                v-for="group in presetsByMode"
                :key="group.mode"
                class="flex-1 flex flex-col min-w-0"
              >
                <div class="px-3 py-1.5 bg-surface-container-lowest border-b border-border">
                  <span class="font-mono text-[0.6rem] font-bold uppercase tracking-widest text-outline">{{ group.label }}</span>
                </div>
                <div
                  v-for="preset in group.presets"
                  :key="preset.preset_id"
                  class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-container-high transition-colors border-b border-border"
                  @click="addPreset(preset)"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-mono text-xs font-bold text-on-surface truncate">{{ preset.name }}</div>
                    <div v-if="preset.rx_freq || preset.tx_freq" class="font-mono text-[0.6rem] text-outline mt-0.5">
                      <span v-if="preset.rx_freq">RX {{ preset.rx_freq }}</span>
                      <span v-if="preset.rx_freq && preset.tx_freq"> / </span>
                      <span v-if="preset.tx_freq">TX {{ preset.tx_freq }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div
            v-else
            class="px-6 py-3 font-mono text-xs text-outline"
          >
            No agency presets available
          </div>
          <div class="border-t border-border mt-1">
            <button
              type="button"
              class="w-full text-left flex items-center gap-2 px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-primary hover:bg-surface-container-high transition-colors font-bold"
              @click="openCustomForm"
            >
              <span class="material-symbols-outlined text-[16px]">add</span>
              Custom channel…
            </button>
          </div>
        </div>
      </template>

      <!-- Custom channel form view -->
      <template v-else>
        <div class="flex flex-col gap-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="col-span-2">
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Channel / System Name *</label>
              <FosInput v-model="customForm.name" placeholder="e.g. VTAC36R" />
            </div>
            <div>
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Mode</label>
              <FosSelect
                :model-value="customForm.mode"
                :options="MODE_OPTIONS"
                @update:model-value="customForm.mode = $event"
              />
            </div>
            <div>
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">RX Freq</label>
              <FosInput v-model="customForm.rx_freq" placeholder="e.g. 155.340" />
            </div>
            <div>
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">RX Tone/NAC</label>
              <FosInput v-model="customForm.rx_tone" placeholder="e.g. 100.0" />
            </div>
            <div>
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">TX Freq</label>
              <FosInput v-model="customForm.tx_freq" placeholder="e.g. 155.340" />
            </div>
            <div>
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">TX Tone/NAC</label>
              <FosInput v-model="customForm.tx_tone" placeholder="e.g. 100.0" />
            </div>
            <div class="col-span-2">
              <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Remarks</label>
              <FosInput v-model="customForm.remarks" placeholder="Optional notes" />
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <template v-if="!showCustomForm">
          <FosButton variant="secondary" size="sm" @click="closeDialog">Cancel</FosButton>
        </template>
        <template v-else>
          <FosButton variant="secondary" size="sm" @click="showCustomForm = false">Back</FosButton>
          <FosButton size="sm" :disabled="!customForm.name.trim()" @click="addCustomChannel">Add Channel</FosButton>
        </template>
      </template>
    </FosDialog>

    <!-- ICP location + phone -->
    <div class="grid grid-cols-2 gap-3">
      <div class="flex flex-col gap-1.5 col-span-2 sm:col-span-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">
          ICP Location <span class="font-normal normal-case tracking-normal text-outline/50">(optional)</span>
        </label>
        <FosInput
          v-model="wizard.step4.icp_location"
          placeholder="Coordinates or address"
        />
      </div>
      <div class="flex flex-col gap-1.5 col-span-2 sm:col-span-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">
          ICP Phone <span class="font-normal normal-case tracking-normal text-outline/50">(optional)</span>
        </label>
        <FosInput
          v-model="wizard.step4.icp_phone"
          placeholder="e.g. 555-867-5309"
          type="tel"
        />
      </div>
    </div>

    <p class="font-mono text-[0.65rem] text-outline">
      Channels populate the ICS 205 comms plan.
    </p>

    <!-- Navigation -->
    <div class="flex justify-between pt-2">
      <FosButton variant="secondary" @click="wizard.currentStep = 3">← Back</FosButton>
      <FosButton @click="wizard.currentStep = 'review'">Review & launch →</FosButton>
    </div>
  </div>
</template>
